import React, { memo, useState } from 'react'
import { AnimatePresence, LayoutGroup, motion, cubicBezier } from 'framer-motion'
import { Flex } from '@chilipiper/design-system'
import { ReplyButtonsBlock as ReplyButtonsBlockType } from '../../../chat-data/types'
import { useExpiredSession } from '../../ExpiredSessionContext'
import { StyledReply } from './styles'

interface Props {
  block: ReplyButtonsBlockType
  onReplyButtonClick: (index: number) => void
  isLastBlock: boolean
}

const easing = cubicBezier(0.42, 0, 1, 1)

export const ReplyButtonsBlock = memo(({ block, onReplyButtonClick, isLastBlock }: Props) => {
  const [selectedReply, setSelectedReply] = useState<number>()
  const isExpiredSession = useExpiredSession()
  const isNoneSelected = block.selected === undefined && selectedReply === undefined
  const isButtonSelected = (index: number) =>
    block.selected === index || (block.selected === undefined && selectedReply === index)

  return (
    <LayoutGroup>
      <motion.div
        animate={{ opacity: 1 }}
        initial={{ opacity: 0 }}
        transition={{ delay: 0.1 }}
        style={{ width: '100%' }}
      >
        <Flex justifyContent='end' flexWrap='wrap' gap={2}>
          <AnimatePresence>
            {block.replies.map((reply, index) => {
              if (isButtonSelected(index) || isNoneSelected) {
                return (
                  <motion.div
                    key={index}
                    exit={{ opacity: 0 }}
                    layoutId={isLastBlock ? `reply-button-${block.id}-${index}` : undefined}
                    transition={{
                      duration: 0.15,
                      ease: easing,
                    }}
                  >
                    <StyledReply
                      as='button'
                      data-test-id='WidgetReplyButtonsBlock'
                      type='button'
                      onClick={() => {
                        setSelectedReply(index)
                        onReplyButtonClick(index)
                      }}
                      disabled={isButtonSelected(index) || isExpiredSession}
                      px={4}
                      py='14px'
                      textStyle={isButtonSelected(index) ? 'body-multiline' : 'multiline-button'}
                    >
                      {reply.text}
                    </StyledReply>
                  </motion.div>
                )
              }
            })}
          </AnimatePresence>
        </Flex>
      </motion.div>
    </LayoutGroup>
  )
})

ReplyButtonsBlock.displayName = 'ReplyButtonsBlock'
